import "styles/questionnaire/arraySummarySection.less";

import React, { useContext, useEffect, useState } from "react";

import { CheckCircleOutlined, DownOutlined, ExclamationCircleOutlined, UpOutlined } from "@ant-design/icons";
import { Button, Card, Drawer, Flex, Modal, Tooltip, Typography } from "antd";
import { AppContext } from "App";
import close from "assets/images/new-questionnaire/close.svg";
import deleteIcon from "assets/images/new-questionnaire/employment/employment-delete.svg";
import { CategoryTitle, ProfileType, QuestionType } from "enums/questionnaire";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import {
    setArrayItemAnswers,
    setInnerArrayItemAnswers,
    setIsEditingArrayItem,
    setIsEditingInnerArrayItem,
} from "redux/reducers/arrayQuestion";
import { next, setCurrentArrayQuestionIndex, setCurrentInnerArrayQuestionIndex } from "redux/reducers/navigation";
import { setCurrentProfile, setCurrentProfileAnswers, setIsEditingProfile } from "redux/reducers/taxProfile";
import { RootState } from "redux/store";
import { IUser } from "types/auth";
import { ArrayAnswersType } from "types/questionnaire";

import { QuestionID } from "../../constants/questionIds";
import { categories, Question } from "../../constants/questions";
import HmrcAuth from "../HmrcIntegration/HmrcAuth";

interface ArraySummarySectionProps {
    data: Question;
    handleQuestionHelpOpenModal?: () => void;
    handleAddButtonClick: () => void;
    handleEditButtonClick: (_index: number) => void;
    handleDeleteButtonClick: (_index: number) => void;
    profileAnswers: ArrayAnswersType[];
    arrayItemsNames: string[];
    currentCategoryTitle: CategoryTitle;
    user: IUser
}
const AuthContainerModal = ({
    isMobile,
    isOpen,
    onClose,
    children,
}: {
    isMobile: boolean;
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
}) =>
    (isMobile ? (
        <Drawer
            className="array-summary-section__drawer"
            placement="bottom"
            height="55%"
            onClose={onClose}
            open={isOpen}
            style={{
                borderRadius: "36px 36px 0 0",
                overflow: "hidden",
                padding: "0px",
                backgroundColor: "#F7F7F7",
            }}
            closeIcon={
                <div className="document-parse-prompt-close">
                    <img src={close} alt="close" className="dblock" />
                </div>
            }
        >
            {children}
        </Drawer>
    ) : (
        <Modal
            onCancel={onClose}
            open={isOpen}
            footer={null}
            width={400}
            centered
        >
            {children}
        </Modal>
    ));
const ArraySummarySection: React.FC<ArraySummarySectionProps> = props => {
    const {
        data,
        handleQuestionHelpOpenModal,
        handleAddButtonClick,
        handleEditButtonClick,
        handleDeleteButtonClick,
        profileAnswers,
        arrayItemsNames,
        currentCategoryTitle,
        user,
    } = props;
    const { isMobile } = useContext(AppContext);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const { incompleteProfiles } = useAppSelector((state: RootState) => ({
        incompleteProfiles: state.taxProfile.incompleteProfiles,
    }));

    const { currentCategory, currentStep, currentQuestion } = useAppSelector((state: RootState) => ({
        currentCategory: state.navigation.currentCategory,
        currentStep: state.navigation.currentStep,
        currentQuestion: state.navigation.currentQuestion,
    }));

    const { taxProfiles } = useAppSelector((state: RootState) => ({
        taxProfiles: state.taxProfile.taxProfiles,
    }));

    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const toggleCard = (index: number) => {
        setActiveIndex(prev => (prev === index ? null : index));
    };

    const isSectionDisabled = (
        questionId: QuestionID | string,
        itemId: string | undefined
    ) => {
        if (!profileAnswers || !itemId) return true;

        const scopedAnswers: any[] = [];
        const matchingProfile = profileAnswers.find(profile => profile.itemId === itemId);
        if (!matchingProfile) return true;

        const flatten = (value: any) => {
            if (Array.isArray(value)) {
                value.forEach(item => flatten(item));
            } else if (value && typeof value === "object") {
                if ("id" in value) scopedAnswers.push(value);
                Object.values(value).forEach(v => flatten(v));
            }
        };

        flatten(matchingProfile.answers);

        return !scopedAnswers.some(answer => answer?.id === questionId);
    };

    const getSubSectionsForProfile = (
        profile: ArrayAnswersType,
        incomeId: QuestionID,
        expensesId?: QuestionID
    ) => {
        const children = [
            {
                key: profile.answers[0]?.id,
                label: <span className="ml6">General Questions</span>,
                disabled: false, // Always enabled unless parent is disabled, which we'll handle outside
            },
            {
                key: incomeId,
                label: <span className="ml6">Income</span>,
                disabled: isSectionDisabled(incomeId, profile.itemId),
            },
        ];

        const rentalType = profile.answers.find(a => a.id === "RENTAL_TYPE")?.answer;
        if (expensesId && rentalType !== ProfileType.rentARoom) {
            children.push({
                key: expensesId,
                label: <span className="ml6">Expenses / Benefits</span>,
                disabled: isSectionDisabled(expensesId, profile.itemId),
            });
        }

        return children;
    };
    const getGeneralChildren = (profile: ArrayAnswersType) => [
        {
            key: profile.answers[0]?.id,
            label: <span className="ml6">General Questions</span>,
            disabled: false,
        },
    ];

    const getIncomeIdForRentalProfile = (profile: ArrayAnswersType): QuestionID => {
        const rentalType = profile.answers.find(a => a.id === "RENTAL_TYPE")?.answer;
        return rentalType !== ProfileType.rentARoom
            ? "RENTAL_MANUAL_INCOME"
            : "RENTAL_GROSS_INCOME";
    };

    const generateChildrenForCategory = (profileSection: CategoryTitle, isTaxProfile: boolean = true) => {
        if (!isTaxProfile || profileSection === CategoryTitle.General) {
            return (profile: ArrayAnswersType) => getGeneralChildren(profile);
        }
        switch (profileSection) {
        case CategoryTitle.SelfEmployment:
            return (profile: ArrayAnswersType) =>
                getSubSectionsForProfile(
                    profile,
                    "SELF_EMPLOYMENT_INCOME_SUMMARY",
                    "SELF_EMPLOYMENT_EXPENSES_SUMMARY"
                );
        case CategoryTitle.CIS:
            return (profile: ArrayAnswersType) =>
                getSubSectionsForProfile(
                    profile,
                    "CIS_EMPLOYMENT_INCOME_SUMMARY",
                    "CIS_EMPLOYMENT_EXPENSES_SUMMARY"
                );
        case CategoryTitle.Employment:
            return (profile: ArrayAnswersType) =>
                getSubSectionsForProfile(
                    profile,
                    "EMPLOYMENT_INCOME_SUMMARY",
                    "EMPLOYMENT_BENEFITS_CARD"
                );
        case CategoryTitle.Rental:
            return (profile: ArrayAnswersType) =>
                getSubSectionsForProfile(
                    profile,
                    getIncomeIdForRentalProfile(profile),
                    "RENTAL_EXPENSES_SUMMARY"
                );
        case CategoryTitle.Dividends:
            return (profile: ArrayAnswersType) =>
                getSubSectionsForProfile(profile, "DIVIDENDS_AMOUNT");
        case CategoryTitle.CapitalGains:
            return (profile: ArrayAnswersType) =>
                getSubSectionsForProfile(profile, "CAPITAL_GAIN_LOSS_ASSET_VALUES");
        case CategoryTitle.InterestIncome:
            return (profile: ArrayAnswersType) =>
                getSubSectionsForProfile(profile, "INTEREST_AMOUNT");
        default:
            return undefined;
        }
    };
    const [expandedSteps, setExpandedSteps] = useState<Record<number, number | null>>({});

    const stripHTML = (input: string): string => {
        const div = document.createElement("div");
        div.innerHTML = input;
        return div.textContent || div.innerText || "";
    };

    const formatQuestion = (question: {id: string, question: string, answer: any, type: string}, name: string): string => {
        if (!question) {
            return "-";
        }

        let rawText = question?.question;

        // Replace [_itemName_] placeholder if it exists and itemName is available
        if (rawText.includes("[_itemName_]")) {
            rawText = rawText.replace(/\[_itemName_\]/g, name);
        }

        const stripped = stripHTML(rawText).trim();
        return stripped.length > 0 ? stripped : "-";
    };
    const formatKey = (key: string): string => {
        return key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str: string) => str.toUpperCase())
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const formatAnswer = (answer: any): string => {
        if (answer === null || answer === undefined) {
            return "-";
        }

        if (typeof answer === "string") {
            const stripped = stripHTML(answer).trim();
            if (stripped.length === 0) return "-";
            const formattedValue = stripped.charAt(0).toUpperCase() + stripped.slice(1).toLowerCase();
            return formattedValue;
        }

        if (typeof answer === "number" || typeof answer === "boolean") {
            return String(answer);
        }

        if (Array.isArray(answer)) {
            const formattedArray = answer.map(item => formatAnswer(item)).filter(str => str !== "-");
            return formattedArray.length > 0 ? formattedArray.join(", ") : "-";
        }

        if (Array.isArray(answer)) {
            const formattedArray = answer.map(item => formatAnswer(item)).filter(str => str !== "-");
            return formattedArray.length > 0 ? formattedArray.join(", ") : "-";
        }
        if (typeof answer === "object") {
            if ("label" in answer && typeof answer.label === "string") {
                return stripHTML(String(answer.label)).trim() || "-";
            }

            const entries = Object.entries(answer)
                .filter(([key]) => key !== "docs")
                .map(([key, val]) => {
                    const formattedVal = formatAnswer(val);
                    const formattedKey = formatKey(key);
                    return formattedVal !== "-" ? `${formattedKey}: ${formattedVal}` : null;
                })
                .filter(Boolean);
            return entries.length > 0 ? entries.join(", ") : "-";
        }

        return "-";
    };

    const getAnswersForStep = (profile: ArrayAnswersType, steps: any[], stepIndex: number) => {
        const allAnswers = profile.answers || [];

        const currentStepId = steps[stepIndex]?.key;
        const nextStepId = steps[stepIndex + 1]?.key;

        let startIndex = allAnswers.findIndex(ans => ans.id === currentStepId);
        let endIndex = nextStepId ? allAnswers.findIndex(ans => ans.id === nextStepId) : allAnswers.length;

        // Handle edge cases (id not found)
        if (startIndex === -1) startIndex = 0;
        if (endIndex === -1) endIndex = allAnswers.length;

        return allAnswers.slice(startIndex, endIndex);
    };

    const toggleStep = (cardIndex: number, stepIndex: number) => {
        setExpandedSteps(prev => ({
            ...prev,
            [cardIndex]: prev[cardIndex] === stepIndex ? null : stepIndex,
        }));
    };
    const goToArrayQuestion = (questionId: string, itemId: string | undefined, answers: any[]) => {
        const currentProfileQuestion = categories[currentCategory]?.steps?.[currentStep]?.questions?.[currentQuestion]?.questions?.findIndex(q => q.id === questionId);
        const currentQuestionListQuestion = categories[currentCategory]?.steps?.[currentStep]?.questions?.[currentQuestion]?.questions?.findIndex(q => q.questions?.some(c => c.id === questionId));
        const currentQuestionOrCardIndex = currentProfileQuestion !== -1 ? currentProfileQuestion : currentQuestionListQuestion;
        if (itemId) {
            dispatch(next({
                nextCategory: currentCategory,
                nextStep: currentStep,
                nextQuestion: currentQuestion,
                nextProfileQuestion: currentQuestionOrCardIndex ?? 0,
            }));

            const currentProfile = taxProfiles?.find(profile => profile._id === itemId);
            dispatch(setCurrentProfile(currentProfile));
            dispatch(setCurrentProfileAnswers(answers));
            dispatch(setIsEditingProfile(true));
        } else if (data.type === QuestionType.arrayQuestionV3) {
            dispatch(next({
                nextCategory: currentCategory,
                nextStep: currentStep,
                nextQuestion: currentQuestion,
                nextProfileQuestion: currentQuestionOrCardIndex ?? 0,
            }));

            dispatch(setCurrentArrayQuestionIndex(currentQuestionOrCardIndex ?? 0));
            dispatch(setArrayItemAnswers(answers));
            dispatch(setIsEditingArrayItem(true));
        } else {
            const currentInnerArrayQuestion = data.questions?.findIndex(q => q.id === questionId);
            const currentInnerArrayCardQuestion = data.questions?.findIndex(q => q.questions?.some(c => c.id === questionId));
            const currentInnerQuestionOrCardIndex = currentInnerArrayQuestion !== -1 ? currentInnerArrayQuestion : currentInnerArrayCardQuestion;
            dispatch(setCurrentInnerArrayQuestionIndex(currentInnerQuestionOrCardIndex ?? 0));
            dispatch(setInnerArrayItemAnswers(answers));
            dispatch(setIsEditingInnerArrayItem(true));
        }
    };
    const [isHmrcFailModalVisible, setIsHmrcFailModalVisible] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const toggleDrawer = (open: boolean | undefined) => {
        setIsDrawerOpen(prev => (typeof open === "boolean" ? open : prev));
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("hmrcFail") === "true") {
            setIsHmrcFailModalVisible(true);
        }
    }, [location]);

    const handleCloseHmrcFailModal = () => {
        setIsHmrcFailModalVisible(false);
        const params = new URLSearchParams(location.search);
        params.delete("hmrcFail");

        navigate({
            pathname: location.pathname,
            search: params.toString(),
        }, { replace: true });
    };

    return (
        <div className="array-summary-section">
            {data.questionTitle && (
                <div className="new-questionnaire__list-question-title">{data.questionTitle}</div>
            )}
            <div className="array-summary-section__question">{data.question}</div>
            {data.infoText && (
                <div className="array-summary-section__info">
                    {data.infoText}
                    {data.promptModal?.showModalOpener && (
                        <span className="shared__learn-more" onClick={handleQuestionHelpOpenModal}>
                            Learn more
                        </span>
                    )}
                </div>
            )}
            {data.id === "EMPLOYMENT_PROFILES" && (
                <>
                    {data.addHmrcButtonText && (
                        <div className="array-summary-section__info">
                            <button
                                className="array-summary-section__add-hmrc-button mt20"
                                onClick={() => toggleDrawer(true)}
                            >
                                {data.addHmrcButtonText}
                            </button>
                        </div>
                    )}

                    <AuthContainerModal
                        isMobile={isMobile}
                        isOpen={isDrawerOpen}
                        onClose={() => toggleDrawer(false)}
                    >
                        <HmrcAuth user={user} />
                    </AuthContainerModal>

                    <AuthContainerModal
                        isMobile={isMobile}
                        isOpen={isHmrcFailModalVisible}
                        onClose={handleCloseHmrcFailModal}
                    >
                        <div className="hmrc-auth__title">Your HMRC Details Aren’t Ready Yet</div>
                        <div className="hmrc-auth__container">
                            <div className="hmrc-auth__subtitle">
                    We’re not able to get the latest details from HMRC just yet. Their systems can take a while to update.
                            </div>
                            <div className="hmrc-auth__subtitle">
                    The quickest way to keep things moving is to enter your employment details in manually.
                            </div>
                        </div>
                        <button
                            className="shared__primary-button"
                            onClick={handleAddButtonClick}
                            style={{ width: "100%", marginTop: "20px" }}
                        >
                Enter Details Manually Now
                        </button>
                    </AuthContainerModal>
                </>
            )}
            {(data.id !== "CIS_EMPLOYMENT_PROFILES" || profileAnswers.length === 0) && <button
                className="array-summary-section__add-button mt20"
                onClick={handleAddButtonClick}
            >
                {data.addButtonText}
            </button>}
            <div className="array-summary-section__items">
                {profileAnswers.map((_item: ArrayAnswersType, index: number) => {
                    const profileItemId = _item.itemId;
                    const isIncomplete =
                        incompleteProfiles?.some(p => p.itemId === profileItemId) ||
                        _item.isCompleted === false;

                    const isActive = activeIndex === index;

                    return (
                        <Card
                            key={index}
                            className={`array-summary-section__item-card ${isActive ? "active" : ""}`}
                            bordered
                            bodyStyle={isActive ? {} : { padding: 0, height: 0, overflow: "hidden" }} // 👈 this
                            title={
                                <div className="array-summary-section__card-header">
                                    <div
                                        className="array-summary-section__card-header__name"
                                        onClick={() => toggleCard(index)}
                                    >
                                        {isIncomplete ? (
                                            <Tooltip title="Profile is incomplete">
                                                <ExclamationCircleOutlined className="array-summary-section__progress-icon warning" />
                                            </Tooltip>
                                        ) : (
                                            <CheckCircleOutlined className="array-summary-section__progress-icon primary-color" />
                                        )}
                                        <Flex vertical>
                                            <Typography.Text ellipsis style={{ maxWidth: 120 }}>{arrayItemsNames[index]}</Typography.Text>
                                            <span className="array-summary-section__card-header__progress">{isIncomplete ? "In Progress" : "Complete"}</span>
                                        </Flex>

                                        {isActive ? <UpOutlined /> : <DownOutlined />}
                                    </div>

                                    <button
                                        className={
                                            isIncomplete
                                                ? "shared__primary-button shared__primary-button--small"
                                                : "shared__secondary-button shared__secondary-button--small"
                                        } onClick={e => {
                                            e.stopPropagation();
                                            if (isIncomplete) {
                                                handleEditButtonClick(index);
                                            } else {
                                                toggleCard(index);
                                            }
                                        }}
                                    >{isIncomplete ? "Complete" : isActive ? "Close" : "See Details"}
                                    </button>
                                </div>
                            }
                        >
                            {isActive && (() => {
                                const isTaxProfile = taxProfiles?.some(profile => profile._id === _item.itemId);
                                const steps = generateChildrenForCategory(currentCategoryTitle, isTaxProfile)?.(_item) || [];
                                return (
                                    <div className="array-summary-section__item-content">
                                        {steps.map((step: any, stepIndex: number) => {
                                            const stepIsExpanded = expandedSteps[index] === stepIndex;
                                            return (
                                                <div key={step.key} className={`array-summary-section__custom-step ${step.disabled ? "disabled" : ""}`}>
                                                    <div
                                                        className="array-summary-section__custom-step-header space-between"
                                                        onClick={() => (!step.disabled ? toggleStep(index, stepIndex) : undefined)}
                                                    >
                                                        <div className="center">
                                                            <div className={`array-summary-section__custom-step-dot ${step.disabled ? "disabled" : "active"}`} />
                                                            <Typography.Text>{step.label}</Typography.Text>
                                                        </div>

                                                        {stepIsExpanded ? <UpOutlined /> : <DownOutlined />}
                                                    </div>
                                                    {stepIsExpanded && (
                                                        <div className="array-summary-section__custom-step-content-wrapper">
                                                            {getAnswersForStep(_item, steps, stepIndex).map(q => (
                                                                <div className="array-summary-section__custom-step-content" key={q.id}>
                                                                    <div key={q.id} className="flex-column">
                                                                        <Typography.Text>{formatQuestion(q, arrayItemsNames[index])}</Typography.Text>
                                                                        <Typography.Text strong>
                                                                            {formatAnswer(q.answer)}
                                                                        </Typography.Text>
                                                                    </div>
                                                                    <Button type="text" className="secondary-color" onClick={() => goToArrayQuestion(q.id, _item.itemId, _item.answers)}>Edit</Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        <Button
                                            danger
                                            type="text"
                                            className="horizontal-center red-color mlAuto mt10"
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleDeleteButtonClick(index);
                                            }}
                                            icon={<img height="auto" src={deleteIcon} alt="delete" />}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                );
                            })()}

                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

export default ArraySummarySection;
